/**
 * PlatformSettingsPage - Parametres de la Plateforme SaaS
 * 
 * Gere les informations vendeur utilisees dans les factures SaaS:
 * - Devises systeme (gestion centralisee) [NOUVEL ONGLET]
 * - Informations societe (nom, adresse, contact)
 * - Informations legales (ICE, RC, IF, etc.)
 * - Parametres de facturation (TVA, devise, banque)
 * - Notes de facture
 */

import React, { useState, useEffect } from 'react';
import {
  Building, Shield, CreditCard, FileText, Save, RefreshCw,
  AlertCircle, Globe, Layout
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { api } from '../../App';
import { toast } from 'sonner';
import SystemCurrenciesSection from '../../components/admin/SystemCurrenciesSection';
import LandingPageCMS from '../../components/admin/LandingPageCMS';

const PlatformSettingsPage = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('currencies');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settings');
      setSettings(data.flat || {});
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des parametres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', { settings });
      toast.success('Parametres enregistres avec succes');
      setHasChanges(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  // Field definitions for vendor info
  const vendorFields = [
    { key: 'vendor.name', label: 'Nom de la societe', placeholder: 'My Riad Manager', required: true },
    { key: 'vendor.address', label: 'Adresse', placeholder: '123 Rue du Commerce, Gueliz' },
    { key: 'vendor.city', label: 'Ville', placeholder: 'Marrakech' },
    { key: 'vendor.postal_code', label: 'Code postal', placeholder: '40000' },
    { key: 'vendor.country', label: 'Pays', placeholder: 'Maroc' },
    { key: 'vendor.phone', label: 'Telephone', placeholder: '+212 5XX XXX XXX' },
    { key: 'vendor.email', label: 'Email facturation', placeholder: 'facturation@myriadmanager.com', type: 'email' },
    { key: 'vendor.website', label: 'Site web', placeholder: 'https://myriadmanager.com' }
  ];

  const legalFields = [
    { key: 'legal.ice', label: 'ICE', placeholder: 'Identifiant Commun de l\'Entreprise', required: true, help: '15 chiffres' },
    { key: 'legal.rc', label: 'RC', placeholder: 'Registre de Commerce', required: true },
    { key: 'legal.if', label: 'IF', placeholder: 'Identifiant Fiscal', required: true },
    { key: 'legal.cnss', label: 'CNSS', placeholder: 'Numero CNSS' },
    { key: 'legal.patent', label: 'Patente', placeholder: 'Numero de Patente' },
    { key: 'legal.capital', label: 'Capital social', placeholder: '100 000 MAD' }
  ];

  const billingFields = [
    { key: 'billing.currency', label: 'Devise', placeholder: 'MAD', required: true },
    { key: 'billing.vat_rate', label: 'Taux TVA (%)', placeholder: '20', type: 'number', required: true },
    { key: 'billing.payment_terms', label: 'Delai de paiement (jours)', placeholder: '30', type: 'number' },
    { key: 'billing.bank_name', label: 'Banque', placeholder: 'Attijariwafa Bank' },
    { key: 'billing.bank_iban', label: 'IBAN / RIB', placeholder: 'MA00 0000 0000 0000 0000 0000' },
    { key: 'billing.bank_bic', label: 'Code SWIFT/BIC', placeholder: 'BCMAMAMC' }
  ];

  const invoiceFields = [
    { key: 'invoice.footer_note', label: 'Mention legale / Note de bas de page', placeholder: 'TVA non applicable, art. 94 du CGI', multiline: true },
    { key: 'invoice.payment_instructions', label: 'Instructions de paiement', placeholder: 'Paiement par virement bancaire sous 30 jours', multiline: true },
    { key: 'invoice.thank_you_note', label: 'Message de remerciement', placeholder: 'Merci pour votre confiance !', multiline: true }
  ];

  const renderFields = (fields) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {fields.map((field) => (
        <div 
          key={field.key} 
          className={field.multiline ? 'md:col-span-2' : ''}
        >
          <Label 
            htmlFor={field.key}
            className="text-sm font-medium text-slate-700 flex items-center gap-2"
          >
            {field.label}
            {field.required && <span className="text-red-500">*</span>}
          </Label>
          {field.help && (
            <p className="text-xs text-slate-400 mt-0.5 mb-1">{field.help}</p>
          )}
          {field.multiline ? (
            <Textarea
              id={field.key}
              data-testid={`setting-${field.key}`}
              value={settings[field.key] || ''}
              onChange={(e) => updateSetting(field.key, e.target.value)}
              placeholder={field.placeholder}
              className="mt-1.5 min-h-[100px] resize-none"
            />
          ) : (
            <Input
              id={field.key}
              data-testid={`setting-${field.key}`}
              type={field.type || 'text'}
              value={settings[field.key] || ''}
              onChange={(e) => updateSetting(field.key, e.target.value)}
              placeholder={field.placeholder}
              className="mt-1.5"
            />
          )}
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="platform-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Parametres Plateforme</h1>
          <p className="text-slate-500 mt-1">
            Configuration globale du SaaS (devises, facturation, informations legales)
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <AlertCircle className="w-3 h-3 mr-1" />
              Modifications non sauvegardees
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={fetchSettings}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-flex">
          <TabsTrigger value="currencies" className="gap-2" data-testid="tab-currencies">
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">Devises</span>
          </TabsTrigger>
          <TabsTrigger value="landing" className="gap-2" data-testid="tab-landing">
            <Layout className="w-4 h-4" />
            <span className="hidden sm:inline">Landing</span>
          </TabsTrigger>
          <TabsTrigger value="vendor" className="gap-2" data-testid="tab-vendor">
            <Building className="w-4 h-4" />
            <span className="hidden sm:inline">Societe</span>
          </TabsTrigger>
          <TabsTrigger value="legal" className="gap-2" data-testid="tab-legal">
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Legal</span>
          </TabsTrigger>
          <TabsTrigger value="billing" className="gap-2" data-testid="tab-billing">
            <CreditCard className="w-4 h-4" />
            <span className="hidden sm:inline">Facturation</span>
          </TabsTrigger>
          <TabsTrigger value="invoice" className="gap-2" data-testid="tab-invoice">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Mentions</span>
          </TabsTrigger>
        </TabsList>

        {/* Currencies Tab - New! */}
        <TabsContent value="currencies">
          <SystemCurrenciesSection />
        </TabsContent>

        {/* Landing Page CMS Tab */}
        <TabsContent value="landing">
          <LandingPageCMS />
        </TabsContent>

        {/* Vendor Tab */}
        <TabsContent value="vendor">
          <Card className="border-2 border-violet-200">
            <CardHeader className="bg-violet-50 border-b border-violet-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-violet-100 border border-violet-200 flex items-center justify-center">
                  <Building className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <CardTitle className="text-violet-800">Informations Societe</CardTitle>
                  <CardDescription className="text-slate-500 mt-1">
                    Ces informations apparaissent sur les factures SaaS envoyees a vos clients
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {renderFields(vendorFields)}
              <div className="flex justify-end mt-6">
                <Button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Legal Tab */}
        <TabsContent value="legal">
          <Card className="border-2 border-emerald-200">
            <CardHeader className="bg-emerald-50 border-b border-emerald-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 border border-emerald-200 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-emerald-800">Informations Legales</CardTitle>
                  <CardDescription className="text-slate-500 mt-1">
                    Identifiants legaux requis sur les factures au Maroc
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {renderFields(legalFields)}
              <div className="flex justify-end mt-6">
                <Button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing">
          <Card className="border-2 border-blue-200">
            <CardHeader className="bg-blue-50 border-b border-blue-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 border border-blue-200 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-blue-800">Parametres de Facturation</CardTitle>
                  <CardDescription className="text-slate-500 mt-1">
                    Configuration par defaut pour la generation des factures
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {renderFields(billingFields)}
              <div className="flex justify-end mt-6">
                <Button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoice Tab */}
        <TabsContent value="invoice">
          <Card className="border-2 border-amber-200">
            <CardHeader className="bg-amber-50 border-b border-amber-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-amber-800">Mentions Facture</CardTitle>
                  <CardDescription className="text-slate-500 mt-1">
                    Textes personnalises apparaissant sur les factures
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {renderFields(invoiceFields)}
              <div className="flex justify-end mt-6">
                <Button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card className="mt-6 bg-slate-50 border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-slate-700">Apercu sur facture</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white rounded-lg border border-slate-200 p-4 font-mono text-xs text-slate-600">
                <div className="font-bold text-slate-800 mb-1">
                  {settings['vendor.name'] || 'Nom de la societe'}
                </div>
                <div>{settings['vendor.address'] || 'Adresse'}</div>
                <div>
                  {settings['vendor.postal_code'] || 'Code postal'} {settings['vendor.city'] || 'Ville'}, {settings['vendor.country'] || 'Pays'}
                </div>
                <div className="mt-2">
                  <span>Tel: {settings['vendor.phone'] || '-'}</span>
                  <span className="mx-2">|</span>
                  <span>Email: {settings['vendor.email'] || '-'}</span>
                </div>
                <Separator className="my-3" />
                <div className="grid grid-cols-3 gap-2 text-[10px]">
                  <div>
                    <span className="text-slate-400">ICE:</span> {settings['legal.ice'] || '-'}
                  </div>
                  <div>
                    <span className="text-slate-400">RC:</span> {settings['legal.rc'] || '-'}
                  </div>
                  <div>
                    <span className="text-slate-400">IF:</span> {settings['legal.if'] || '-'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PlatformSettingsPage;
